/**
 * id-provenance.js — computes the id-ownership record persisted on a section
 * instance at insert time (Workstream A).
 *
 * `section.originalHtml` is a RESET BASELINE, not provenance: it is copied
 * from the (possibly skinned, always re-stamped) current HTML at insert,
 * rebaselined by variant swaps, and back-filled from user-edited HTML by the
 * legacy migration. Diffing against it misclassifies old user-added ids as
 * template-owned. This module instead records, from the PRISTINE catalog HTML
 * at the moment of insertion:
 *
 *   idProvenance: {
 *     sourceRootId,    // the catalog HTML's root element id (usually null —
 *                      // the parser strips it), for completeness
 *     sourceOwnedIds,  // every id declared anywhere in the catalog HTML
 *     sourceIdRefs     // every id the catalog HTML references (for, aria
 *                      // attrs, href="#…", url(#…) — via the shared registry)
 *   }
 *
 * Export-time classification: an id present in the instance's HTML but absent
 * from sourceOwnedIds is user-authored → never renamed. Instances with no
 * idProvenance (legacy saves) are handled conservatively by the id plan:
 * nothing renamed, ambiguity disclosed.
 *
 * Browser-safe (DOMParser); Node callers (tests, exporters under jsdom) pass
 * a parse function.
 */

import { collectReferencedIds } from './id-reference-registry.js';

function defaultParse(html) {
  if (typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    return doc.body.firstElementChild;
  } catch {
    return null;
  }
}

/**
 * Collect every id DECLARED in an element subtree, root first then DOM order.
 */
export function collectDeclaredIds(root) {
  const ids = [];
  if (!root || typeof root.querySelectorAll !== 'function') return ids;
  const rootId = typeof root.getAttribute === 'function' ? root.getAttribute('id') : null;
  if (rootId) ids.push(rootId);
  for (const el of root.querySelectorAll('[id]')) {
    const id = el.getAttribute('id');
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * Compute the idProvenance record from pristine catalog HTML. Returns null
 * when the HTML cannot be parsed (callers persist nothing rather than a lie —
 * the id plan then treats the instance conservatively).
 *
 * @param {string} html - the catalog section's HTML, BEFORE composition skin
 *   and BEFORE stampEditableIds.
 * @param {{parseHTML?: (html: string) => Element|null}} [options]
 */
export function computeIdProvenance(html, options = {}) {
  if (typeof html !== 'string' || !html.trim()) return null;
  const parse = options.parseHTML || defaultParse;
  const wrapper = parse(html);
  if (!wrapper) return null;
  // defaultParse wraps in a <div>; a caller-supplied parseHTML must do the
  // same (single wrapper element containing the section markup).
  // Scan the WRAPPER so multi-root section HTML (rare but real) contributes
  // every top-level sibling; the wrapper itself carries no id.
  const sectionRoot = wrapper.firstElementChild;
  const rootId =
    sectionRoot && typeof sectionRoot.getAttribute === 'function'
      ? sectionRoot.getAttribute('id')
      : null;
  return {
    sourceRootId: rootId || null,
    sourceOwnedIds: collectDeclaredIds(wrapper),
    sourceIdRefs: [...collectReferencedIds(wrapper)].sort()
  };
}

/** Shape check used by migrateState and the id plan. */
export function isValidIdProvenance(value) {
  return !!(
    value &&
    typeof value === 'object' &&
    Array.isArray(value.sourceOwnedIds) &&
    value.sourceOwnedIds.every((id) => typeof id === 'string') &&
    Array.isArray(value.sourceIdRefs) &&
    value.sourceIdRefs.every((id) => typeof id === 'string') &&
    (value.sourceRootId === null || typeof value.sourceRootId === 'string')
  );
}
